import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { filter, find, has, pickBy, size, toString, sumBy } from "lodash";
import { getDataChunk, selectToDownload, setAsDownloaded, setSelectionMode } from "../../store/dataSlice";
import { deleteShcFile, downloadPackage, uploadContracts, uploadShcFile } from "../../store/uploadSlice";
import { editValues, getProducts, showForm } from "../../store/formSlice";
import { deleteOrgContract, updateContractsDate } from "../../store/api/contracts";
import { deleteOrgLicense, downloadOrgLicense } from "../../store/api/organisations";
import { Modal } from "../../components/Modal";
import { PageContent } from "./PageContent";
import { Page404 } from "../../components/Page404";
import { PAGE_SIZE } from "../../settings";
import { Page403 } from "../../components/Page403";
import { ShcUploadWrapper } from "../shc/ShcUploadWrapper";
import * as tabs from './settings';
import { UpdateContractsDatesModal } from "./UpdateContractsDatesModal";
import { useUrlParams } from "./useUrlParams";
import { createQueryString, fixPackageSize, removeQueryParam, snakeCase } from "../../utils";
import { deleteFilter, getUserSavedFilters, saveFilter, updateFilter } from "../../store/api/users";
import { Paragraph } from "../../components/Text";

const PageContentContainer = ({ location, match, history }) => {
    const { pathname, search } = location;
    let { org, tab, selected } = match.params;
    const urlParts = pathname.split("/"), currentPage = urlParts[1];
    if (currentPage === "contracts" && urlParts[2] === "all") {
        tab = urlParts[2];
    };
    let filterTab = currentPage == tab ? tab : currentPage + "_" + tab;
    const dispatch = useDispatch();
    const { urlParams, setUrlParams } = useUrlParams();
    const { column, order, page, context } = urlParams;
    const { data, details, loading, total, forbidden, notFound, shouldUpdate, selectedItems } = useSelector(state => state.data);
    const { products } = useSelector(state => state.form);
    const savedFilters = useSelector(state => has(state.data.filters, filterTab) ? state.data.filters[filterTab] : []);
    const { grants } = useSelector(state => state.user);

    const initialState = { visible: false, date: null }
    const [ state, setState ] = useState(initialState);

    const getData = () => {
        if (currentPage == "packages" && !products.length) {
            dispatch(getProducts());
        }
        return dispatch(getDataChunk({ path: [tab, currentPage !== tab ? currentPage : null], org: org, selected: selected, search: removeQueryParam(search, ["filter"]) }))
    };

    const confirmRemovingItem = ({ content, values }) => {
        const fs = {
            contracts: deleteOrgContract,
            shc: deleteShcFile,
            licenses: deleteOrgLicense
        };
        let currentTab = currentPage === "contracts" ? currentPage : tab;
        Modal.confirm({
            content: content,
            onOk: () => Object.keys(fs).includes(currentTab)
                    ? dispatch(fs[currentTab](values))
                        .unwrap()
                        .then(() => getData()
                            .unwrap()
                            .then(({ total }) => {
                                if (page > Math.ceil(total/PAGE_SIZE)) {
                                    setQueryParams({ replace: true, page:  Math.ceil(total/PAGE_SIZE) });
                                }
                            })
                            .catch((e) => {}))
                        .catch(() => {})
                    : null
        });
    };

    const confirmDownloadPackages = () => {
        Modal.confirm({
            content: <Paragraph>Are you sure you want to download <b>{size(selectedItems)} package{size(selectedItems) != 1 && "s"}</b> with a total size about <b>{fixPackageSize(sumBy(Object.values(selectedItems), d => d.size))}</b>?</Paragraph>,
            okText: "Yes",
            okButtonProps: {style: { height: "40px", padding: "0px 21px", background: "var(--sp-main-color)", borderRadius: "8px", border: "none" }},
            onOk: () => {
                Object.values(selectedItems).map(d => d.files.map(file =>
                    dispatch(downloadPackage({ key: file, minos: d.minos }))
                    .unwrap()
                    .then(res => grants.isAdmin || res != file ? null : dispatch(setAsDownloaded(res)))
                ));
                dispatch(setSelectionMode());
            }
        })
    };

    let tabProps = {
        ...grants,
        ...details,
        pathname: pathname,
        search: search,
        context: context,
        org: org,
        tab: tab,
        selected: selected,
        filterOptions: urlParams,
        products: products,
        showForm: (key) => dispatch(showForm(key)),
        editValues: (values) => dispatch(editValues(values)),
        deleteItem: (values) => confirmRemovingItem(values),
        uploadContracts: ({ file }) => dispatch(uploadContracts(file)),
        updateContractsDate: () => setState(state => ({ ...state, visible: true })),
        downloadLicense: (id) => dispatch(downloadOrgLicense(id)),
        uploadFile: ({ file }) => dispatch(uploadShcFile(file)),
        selectedPackages: selectedItems,
        selectItem: (id) => dispatch(selectToDownload(id)),
        setSelection: () => dispatch(setSelectionMode()),
        downloadPackages: confirmDownloadPackages
    };

    const onContractsDateUpdate = () => {
        const { date } = state;
        dispatch(updateContractsDate({ contract: details.contractName, endDate: date }));
        setState(initialState);
    };

    let selectedTab = null;
    if (currentPage !== tab && Object.keys(tabs).includes(currentPage) && tabs[currentPage](tabProps)) {
        selectedTab = tabs[currentPage](tabProps);
    } else if (Object.keys(tabs).includes(tab)) {
        selectedTab = tabs[tab](tabProps);
    };

    const setQueryParams = (params={}) => {
        if (params.page && params.page < 2) {
            delete params.page;
            delete urlParams.page;
        }
        setUrlParams({ ...urlParams, ...params });
    };

    const validateQueryParams = (params) => {
        if (selectedTab) {
            let { searchOptions } = selectedTab;
            Object.keys(params).map(key => {
                if (key == "page") {
                    if (page < 2) {
                        delete params.page;
                    }
                } else if (key == "column" || key == "order") {
                    let sortColumns = selectedTab.columns.map(col => col.dataIndex);
                    if (sortColumns.includes(params.column) && params.order) {
                        if (!["asc", "desc"].includes(params.order)) {
                            params.order = "asc";
                        }
                    } else {
                        delete params.column;
                        delete params.order;
                    };
                } else if (Array.isArray(searchOptions) && searchOptions.map(o => o.key).includes(key)) {
                    let option = find(searchOptions, o => o.key == key), { options } = option;
                    if (Array.isArray(options)) {
                        let optionValues = options.map(o => typeof o.key === "string" ? snakeCase(o.key) : o.key);
                        if (Array.isArray(params[key])) {
                            params[key] = filter(params[key], o => optionValues.includes(snakeCase(o)));
                        } else if (!optionValues.includes(params[key])) {
                            delete params[key];
                        }
                    }
                } else if (key != "context" && key != "filter") {
                    delete params[key];
                };
            });
            if (createQueryString(params) == search) {
                return true;
            } else {
                setQueryParams({ replace: true, ...params });
            };
        }
    };

    useEffect(() => {
        dispatch(getUserSavedFilters(filterTab));
    }, [ tab, org ]);

    useEffect(() => {
        // do nothing until current user grant has been retrieved
        if (grants.orgAlias) {
            if (location.state && location.state.from == null) {
                let order = null, column = null;
                if (selectedTab && selectedTab.enabled && selectedTab.columns) {
                    let defaultSortCol = find(selectedTab.columns, col => col.defaultSortOrder);
                    if (defaultSortCol) {
                        column = defaultSortCol.dataIndex;
                        order = defaultSortCol.defaultSortOrder;
                    }
                }
                setQueryParams({ replace: true, column: column, order: order });
            } else if (validateQueryParams(urlParams)) {
                let promise = getData();
                promise
                    .unwrap()
                    .then(({ total }) => {
                        if (page > Math.ceil(total/PAGE_SIZE)) {
                            setQueryParams({ replace: true, page:  Math.ceil(total/PAGE_SIZE) });
                        }
                    })
                    .catch((e) => {})
                return () => promise.abort();
            };
        };
    }, [ currentPage, org, tab, grants.orgAlias, JSON.stringify(urlParams) ]);


    useEffect(() => {
        if (urlParts.includes(shouldUpdate)) {
            getData();
        }
    }, [ shouldUpdate]);

    if (forbidden) {
        return <Page403 />
    };

    const getFilterOptions = (params={}) => {
        if (selectedTab && size(selectedTab.searchOptions)) {
            const { searchOptions } = selectedTab;
            let picked = searchOptions.reduce((a, b) => ({ ...a, [b.key]: params[b.key]}), {});
            return pickBy(picked, o => o !== undefined);
        }
        return {};
    };

    const applyFilter = (key) => {
        let queryFilter = find(Object.values(savedFilters), o => o.id == key);
        if (queryFilter && has(queryFilter, "content")) {
            let position = queryFilter.content.indexOf("?"), filterContent = queryFilter.content.slice(position + 1), newSearch = [`?filter=${key}`];
            if (filterContent != "") {
                newSearch.push(filterContent);
            }
            history.push({
                search: newSearch.join("&")
            })
        };
    };

    const saveQueryFilterAs = ({ name }) => {
        dispatch(saveFilter({ tab: filterTab, name: name, search: removeQueryParam(search, ["filter", "page"]) }))
            .unwrap()
            .then((res) => {
                if (res && has(res, "filterId")) {
                    setQueryParams({ filter: res.filterId });
                };
                dispatch(getUserSavedFilters(filterTab));
            });
    };

    const saveQueryFilter = () => {
        dispatch(updateFilter({ id: urlParams.filter, search: removeQueryParam(search, ["filter", "page"]) }))
            .unwrap()
            .then(() => {
                dispatch(getUserSavedFilters(filterTab));
            });
    };

    const confirmRemovingFilter = (key) => {
        let savedFilter = find(savedFilters, o => o.id == key);
        Modal.confirm({
            content: <Paragraph>Are you sure you want to delete the <b>{savedFilter && has(savedFilter, "name") ? savedFilter.name : ""}</b> filter?</Paragraph>,
            onOk: () => dispatch(deleteFilter(key))
                        .unwrap()
                        .then(() => {
                            dispatch(getUserSavedFilters(filterTab));
                            if (urlParams.filter == key) {
                                delete urlParams.filter;
                                setQueryParams(urlParams);
                            }
                        })
        })
    };

    const isFilterDirty = () => {
        if (urlParams.filter) {
            let queryFilter = find(Object.values(savedFilters), o => o.id == urlParams.filter);
            if (queryFilter && has(queryFilter, "content")) {
                return toString(removeQueryParam(search, ["filter", "page"])).replace(/^\?/, "") != toString(queryFilter.content).replace(/^\?/, "");
            }
        }
        return false;
    };

    if (notFound || !selectedTab) {
        return <Page404 />
    };

    const component = <>
        <PageContent
            data={data || []}
            loading={loading}
            sortColumn={column}
            sortOrder={order}
            currentPage={page || 1}
            total={total}
            context={context}
            setQueryParams={setQueryParams}
            filterOptions={getFilterOptions(urlParams)}
            savedFilters={savedFilters}
            selectedFilter={urlParams.filter}
            applyFilter={(key) => applyFilter(key)}
            saveQueryFilter={saveQueryFilterAs}
            saveFilterChanges={saveQueryFilter}
            deleteQueryFilter={confirmRemovingFilter}
            isFilterDirty={isFilterDirty()}
            {...selectedTab}
        />
        <UpdateContractsDatesModal
            visible={state.visible}
            onCancel={() => setState(initialState)}
            onOk={() => onContractsDateUpdate()}
            onChange={(value) => setState(state =>({ ...state, date: value.format("YYYY-MM-DD") }))}
        />
    </>;

    if (tab === "shc") {
        return <ShcUploadWrapper>{component}</ShcUploadWrapper>
    };

    return component;
};

export { PageContentContainer };